import { type FC, useState } from "react";
import { useField } from "react-final-form";
import { Button, CardContent, Grid, List } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import { ProductTypeType } from "@/types/graphql/partner.generated";
import { useCountryAccessesSelectQuery } from "@/partner/entities/countryAccesses";
import { usePaymentProviderAvailability } from "@/partner/entities/platforms";
import { createEmptyPrice, type ProductFormPrice } from "../model";
import { PriceRow } from "./PriceRow";
import { PriceEditDialog } from "./PriceEditDialog";

interface PricesFieldProps {
  productType: ProductTypeType;
  /** Дефолтный dateFrom новой цены — текущая расчётная publishAt формы
   * (référence: PricesField `startAt` проп из Edit.js), иначе — «сейчас». */
  defaultDateFrom?: string | null;
}

interface EditingState {
  price: ProductFormPrice;
  index: number | null;
}

/**
 * Список цен продукта + диалог создания/редактирования (референс:
 * Edit/PricesField/PricesField.js). Состояние — поле формы "prices"
 * (react-final-form), редактируется целиком через input.onChange, как
 * локальный массив.
 */
export const PricesField: FC<PricesFieldProps> = ({
  productType,
  defaultDateFrom,
}) => {
  const { t } = useTranslation();
  const { input } = useField<ProductFormPrice[]>("prices", {
    subscription: { value: true },
  });
  const prices = input.value ?? [];
  const [editing, setEditing] = useState<EditingState | null>(null);

  const { availableCurrencies, subscriptionCurrencies } =
    usePaymentProviderAvailability();
  const { data: countryAccessData } = useCountryAccessesSelectQuery();
  const countryAccessTitleById = Object.fromEntries(
    (countryAccessData?.countryAccesses?.nodes ?? []).map((item) => [
      item.id,
      item.title,
    ])
  );

  const handleAdd = () => {
    setEditing({
      price: createEmptyPrice(
        productType,
        defaultDateFrom,
        productType === ProductTypeType.SubscriptionType
          ? subscriptionCurrencies
          : availableCurrencies
      ),
      index: null,
    });
  };

  const handleEdit = (index: number) => {
    setEditing({ price: prices[index], index });
  };

  const handleRemove = (index: number) => {
    input.onChange(prices.filter((_, i) => i !== index));
  };

  const handleClose = () => setEditing(null);

  const handleSubmitEdit = (price: ProductFormPrice) => {
    if (editing?.index == null) {
      input.onChange([...prices, price]);
    } else {
      input.onChange(
        prices.map((current, i) => (i === editing.index ? price : current))
      );
    }
    setEditing(null);
  };

  return (
    <Grid container spacing={2}>
      {prices.length > 0 && (
        <Grid size={12}>
          <List disablePadding>
            {prices.map((price, index) => (
              <PriceRow
                key={price.key}
                price={price}
                productType={productType}
                countryAccessTitleById={countryAccessTitleById}
                onEdit={() => handleEdit(index)}
                onRemove={() => handleRemove(index)}
              />
            ))}
          </List>
        </Grid>
      )}

      <Grid size={12}>
        <CardContent sx={{ pt: 0 }}>
          <Button
            variant="outlined"
            fullWidth
            startIcon={<AddIcon />}
            onClick={handleAdd}
          >
            {t("products.form.pricesAddButton")}
          </Button>
        </CardContent>
      </Grid>

      {editing && (
        <PriceEditDialog
          open
          price={editing.price}
          productType={productType}
          isNew={editing.index === null}
          onClose={handleClose}
          onSubmit={handleSubmitEdit}
        />
      )}
    </Grid>
  );
};
